import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { arrayOf, exact, func, number, string } from 'prop-types';
import { Icon } from '@atoms';
import { Flex, Button, Typography } from '@mixins';
import { theme } from '@styles';
import { INIT, FAILURE } from '@constants/requestPhase';

import { ReactComponent as FileIcon } from '@assets/svgs/File.svg';
import { ReactComponent as WhiteFileIcon } from '@assets/svgs/WhiteFile.svg';
import { ReactComponent as TrashCanIcon } from '@assets/svgs/TrashCan.svg';

import { DragDropZone, FileItemContainer, ProgressBar, StatusIcon } from './styles';

const STATUSES = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  COMPLETED: 'completed',
  FAILED: 'failed',
};

const FileUploader = ({ onFileSet, file, onFileSubmit, requestPhase, fileLimits, onFileRemove }) => {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [status, setStatus] = useState(STATUSES.IDLE);
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const isFilePreloaded = useMemo(() => file?.name && file?.size === null, [file]);

  useEffect(() => {
    if (isFilePreloaded) {
      setStatus(STATUSES.COMPLETED);
      setProgress(100);
    }
  }, [isFilePreloaded]);

  useEffect(() => {
    if (requestPhase === FAILURE) {
      setStatus(STATUSES.FAILED);
      setErrorMessage('Failed to upload file. Please try again.');
    }
  }, [requestPhase]);

  useEffect(() => {
    if (progress === 100 && status !== STATUSES.FAILED) {
      setStatus(STATUSES.COMPLETED);
    }
  }, [progress, status]);

  const validateFile = useCallback(
    selectedFile => {
      const fileExtension = selectedFile?.type;

      if (!fileLimits.allowedMimeTypes.includes(fileExtension)) {
        setErrorMessage(
          `Wrong file format. Please upload a file in one of the accepted file formats: ${fileLimits.fileType}`,
        );
        setStatus(STATUSES.FAILED);
        return false;
      }

      if (selectedFile?.size > fileLimits.maxSizeBytes) {
        setErrorMessage('File is too large');
        setStatus(STATUSES.FAILED);
        return false;
      }

      return true;
    },
    [fileLimits],
  );

  const handleFileSelect = useCallback(
    selectedFile => {
      if (!selectedFile) return;

      if (!validateFile(selectedFile)) {
        setStatus(STATUSES.FAILED);
        onFileSet({
          name: selectedFile.name,
          size: selectedFile.size,
        });

        return;
      }

      onFileSet({
        name: selectedFile.name,
        size: selectedFile.size,
      });
      setStatus(STATUSES.UPLOADING);
      setProgress(0);
      setErrorMessage('');

      // Simulate upload progress
      simulateUpload(selectedFile);
    },
    [fileLimits],
  );

  const simulateUpload = selectedFile => {
    onFileSubmit(selectedFile, { type: selectedFile.type });

    let currentProgress = 0;
    const interval = setInterval(() => {
      currentProgress += Math.random() * 40;
      if (currentProgress >= 100) {
        currentProgress = 100;
        clearInterval(interval);
        setProgress(100);
      } else {
        setProgress(Math.floor(currentProgress));
      }
    }, 100);
  };

  const handleDrag = e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleInputChange = e => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleButtonClick = () => {
    inputRef.current?.click();
  };

  const handleReset = useCallback(() => {
    onFileRemove();
    onFileSet(null);
    setStatus(STATUSES.IDLE);
    setProgress(0);
    setErrorMessage('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, []);

  const formatFileSize = useCallback(bytes => {
    const k = 1024;
    const sizeLabel = bytes / k > 1024 ? 'MB' : 'KB';
    const size = bytes / k > 1024 ? bytes / k ** 2 : bytes / k;

    return `${Math.round(size)} ${sizeLabel}`;
  }, []);

  const getStatusBackgroundColor = useCallback(() => {
    if (status === STATUSES.COMPLETED) {
      return theme.color.mintMinded[300];
    }
    if (status === STATUSES.FAILED) {
      return theme.color.red;
    }
    return theme.color.gray[900];
  }, [status]);

  const getStatusText = useCallback(() => {
    if (status === STATUSES.COMPLETED) {
      return 'Upload Completed';
    }
    if (status === STATUSES.FAILED) {
      return 'Upload Failed';
    }
    return 'Uploading...';
  }, [status]);

  return (
    <>
      {status === STATUSES.IDLE && !file && (
        <Flex
          width="100%"
          flexDirection="column"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}>
          <DragDropZone
            isDragActive={dragActive}
            theme={theme}
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            onClick={handleButtonClick}>
            <Icon mb={16} SVG={FileIcon} width={50} height={50} />
            <Typography fontSize={[14, 16, 18]} variant="diatype-500" color={theme.color.gray[500]}>
              Select a file or drag and drop here
            </Typography>
            <Typography mb={16} fontSize={[10, 12, 14]} variant="diatype-300" color={theme.color.gray[200]}>
              {fileLimits.fileType}, file size no more than {formatFileSize(fileLimits.maxSizeBytes)}
            </Typography>
            <Button
              variant="secondary"
              padding="8px 16px"
              onClick={e => {
                e.stopPropagation();
                handleButtonClick();
              }}>
              Select file
            </Button>
          </DragDropZone>
          <input ref={inputRef} type="file" hidden onChange={handleInputChange} />
        </Flex>
      )}
      {status !== STATUSES.IDLE && (
        <FileItemContainer status={status} theme={theme} flexDirection="column" alignItems="flex-start">
          <Flex mb={10} width="100%" alignItems="center">
            <StatusIcon mr={10} status={status} theme={theme}>
              <Icon SVG={WhiteFileIcon} width={20} height={20} />
            </StatusIcon>
            <Flex flexDirection="column" flex={1}>
              <Typography fontSize={[12, 14]} variant="diatype-400" color={theme.color.gray[500]}>
                {file?.name}
              </Typography>
              <Flex alignItems="center">
                {file?.size !== null && (
                  <>
                    <Typography mr="6px" fontSize={[10, 12]} color={theme.color.gray[300]}>
                      {formatFileSize(file?.size)}
                    </Typography>
                    <Typography mr="6px" fontSize={[10, 12]} color={theme.color.gray[800]}>
                      |
                    </Typography>
                  </>
                )}
                <Typography fontSize={[10, 12]} color={getStatusBackgroundColor()}>
                  {getStatusText()}
                </Typography>
              </Flex>
            </Flex>
            <Icon
              alignSelf="start"
              SVG={TrashCanIcon}
              color={theme.color.mintMinded[900]}
              width={20}
              height={20}
              pointer
              onClick={handleReset}
            />
          </Flex>
          {status !== STATUSES.FAILED && <ProgressBar progress={progress} status={status} theme={theme} />}
          {errorMessage && status === STATUSES.FAILED && (
            <Typography alignSelf="start" fontSize={12} color={theme.color.red} mt="4px">
              *{errorMessage}
            </Typography>
          )}
        </FileItemContainer>
      )}
    </>
  );
};

FileUploader.defaultProps = {
  onFileRemove: () => {},
  requestPhase: INIT,
  file: null,
  onFileSet: null,
  onFileSubmit: null,
  fileLimits: {},
};

FileUploader.propTypes = {
  onFileRemove: func,
  fileLimits: exact({
    allowedMimeTypes: arrayOf(string).isRequired,
    maxSizeBytes: number.isRequired,
    fileType: string.isRequired,
  }),
  requestPhase: string,
  file: exact({
    name: string.isRequired,
    size: number.isRequired,
  }),
  onFileSet: func,
  onFileSubmit: func,
};

export default FileUploader;
